import { SplitButton } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { lockIcon } from '@progress/kendo-svg-icons';
import CustomTitleBar from 'src/components/GuideMeTitleBar';
import Button from 'src/components/design-system/Button';
import {
  CanImportMultipleCompanies,
  CanImportSingleCompany,
} from 'src/components/guards/PermissionsList';
import usePermission from 'src/hooks/usePermissions';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import { informationIcon } from 'src/utils/icons';

const BasicPageNavigation = () => {
  const {
    hasEditCheckbookPermission,
    hasUsesInitialBudgetsPermission,
    hasCanOverrideBudgetsPermission,
    hasManageBudgetPermission,
  } = useViewCheckbook();
  return (
    <ul className="px-4 font-bold text-[13px]">
      <li className="list-disc font-semibold">
        Click on ‘Year View’ to switch to a display of Forecast Amounts for the selected Fiscal
        Year.
      </li>
      <li className="list-disc font-semibold">
        Use the Filter pick-list to switch to views of Active, Inactive, and Deleted GL Codes.
      </li>
      {(hasUsesInitialBudgetsPermission &&
        hasManageBudgetPermission &&
        hasCanOverrideBudgetsPermission) ||
      hasEditCheckbookPermission ? (
        <>
          {hasUsesInitialBudgetsPermission ||
          hasManageBudgetPermission ||
          hasCanOverrideBudgetsPermission ? (
            <>
              <li className="list-disc font-semibold">
                Click
                <Button color="primary" className="inline-block" readonly={true}>
                  Edit
                </Button>
                to modify the GL Code description, the Forecast Amount, the Budget Amount, Fixed GL
                Code flag, Active GL Code flag, to add Journal Entries, to Adjust Forecast Amounts
                by a percentage value, to Adjust Budget Amounts by a percentage value, or to edit GL
                Code attributes.
              </li>
              <li className="list-disc font-semibold">
                Click{' '}
                <SplitButton
                  style={{ borderRadius: 0, fontWeight: 400 }}
                  fillMode={'flat'}
                  className="bg-info !m-1 !text-white pointer-events-none"
                  text="Copy Across"
                  size="small"
                />{' '}
                to copy either the Forecast Amount or the Budget Amount contained in the first
                editable column to all other editable columns on the row. Null value will not be
                copied.
              </li>
            </>
          ) : (
            <>
              <li className="list-disc font-semibold">
                Click
                <Button color="primary" className="inline-block" readonly={true}>
                  Edit
                </Button>
                to modify the GL Code description, the Forecast Amount, Fixed GL Code flag, Active
                GL Code flag, to add Journal Entries, to Adjust Forecast Amounts by a percentage
                value, to Adjust Budget Amounts by a percentage value, or to edit GL Code
                attributes.
              </li>
            </>
          )}
          {!hasEditCheckbookPermission && (
            <>
              <li className="list-disc font-semibold">
                Click
                <Button color="primary" className="inline-block" readonly={true}>
                  Edit
                </Button>
                to modify the GL Code description, the Forecast Amount, the Budget Amount, Fixed GL
                Code flag, Active GL Code flag, to add Journal Entries, to Adjust Forecast Amounts
                by a percentage value, or to Adjust Budget Amounts by a percentage value
              </li>
              <li className="list-disc font-semibold">
                Click{' '}
                <SplitButton
                  style={{ borderRadius: 0, fontWeight: 400 }}
                  fillMode={'flat'}
                  className="bg-info !m-1 !text-white pointer-events-none"
                  text="Copy Across"
                  size="small"
                />{' '}
                to copy either the Forecast Amount or the Budget Amount contained in the first
                editable column to all other editable columns on the row. Null value will not be
                copied.
              </li>
            </>
          )}
          <li className="list-disc font-semibold">
            Use the Checkbox column to select eligible GL Codes for managing attributes, and click{' '}
            <Button color="info" className="inline-block" readonly={true}>
              Bulk Manage GL Code Attributes
            </Button>{' '}
            to manage GL code attribute assignments in bulk
          </li>
        </>
      ) : (
        ''
      )}

      <li className="list-disc font-semibold">
        Click on the
        <Button color="success" className="inline-block" readonly={true}>
          Add New GL Code
        </Button>
        to add a New GL Code for the selected company.
      </li>
      <li className="list-disc font-semibold">
        Use the Checkbox column to select eligible GL Codes for Deletion, and click the{' '}
        <Button color="error" className="inline-block" readonly={true}>
          Delete
        </Button>
        button to delete in bulk.
      </li>
    </ul>
  );
};

const ViewingData = () => (
  <ul className="px-4 font-bold text-[13px]">
    <li className="list-disc font-semibold">
      Choose a period in the 'Select a Period' drop down OR use the next or previous arrow buttons
      to change Accounting Periods.
    </li>
    <li className="list-disc font-semibold">
      Choose a Department from the ‘Select a Department’ drop down to view Accounting Data for a
      specific department.
    </li>
    <li className="list-disc font-semibold">
      <span className="inline-flex items-center">
        Click on <span className="mx-3">{informationIcon}</span>
      </span>
      in the departments column to view the Department(s) associated with the GL Code for that row.
    </li>
    <li className="list-disc font-semibold">
      Click on hyperlinked Planned, Committed or Actualized Amount values to view the respective
      transactions for that Amount.
    </li>
    <li className="list-disc font-semibold">
      <span className="inline-flex items-center">
        Click on <span className="mx-3">{informationIcon}</span>
      </span>{' '}
      in the PO Approval Exceptions column to view the Contacts that have attribute assigned.
    </li>
    <li className="list-disc font-semibold">
      <span className="inline-flex items-center">
        Click on <span className="mx-3">{informationIcon}</span>
      </span>
      in the Invoice Approval Exceptions column to view the Contacts that have attribute assigned.
    </li>
    <li className="list-disc font-semibold">
      <SvgIcon icon={lockIcon} className="mb-1 mr-1" />
      displayed near period(s) in the Year View tab indicates that the period is closed.
    </li>
    <li className="list-disc font-semibold">
      <span className={`bg-[#AAAAAA] text-white w-45 h-13 cursor-not-allowed mr-1`}>
        <span className="m-1">CLOSED</span>
      </span>
      displayed near period(s) in the Select a Fiscal Year dropdown indicates that the period is
      closed.
    </li>
  </ul>
);

const TransactionDetails = () => {
  const isImportable = usePermission({
    permissions: [...CanImportMultipleCompanies, ...CanImportSingleCompany],
    or: true,
  });
  return (
    <ul className="px-4 font-bold text-[13px]">
      <li className="list-disc font-semibold">
        Click on the
        <Button color="secondary" className="inline-block" readonly={true}>
          View
        </Button>
        in the Transaction Log column to view all transactions for the GL Code for the row.
      </li>
      <li className="list-disc font-semibold">
        Click on the
        <Button color="secondary" className="inline-block" readonly={true}>
          View All Logs
        </Button>
        to view all Transactions for all GL Codes associated for the currently selected
        Department(s) and Accounting Period.
      </li>
      <li className="list-disc font-semibold">
        Use the 'Export' option to export data based on applied filters for the current page, or for
        all pages, to an excel file.
      </li>
      <li className="list-disc font-semibold">
        Click on the
        <Button color="secondary" className="inline-block" readonly={true}>
          View All Attribute Contact Assignments
        </Button>{' '}
        to view all contacts and their GL code attribute assignments.
      </li>
      <li className="list-disc font-semibold">
        Use Export tab in Checkbook Export tool to export GL code data as .xls or .txt files.
      </li>
      {isImportable && (
        <>
          <li className="list-disc font-semibold">
            Click on the{' '}
            <Button color="secondary" className="inline-block" readonly={true}>
              Import/Export
            </Button>{' '}
            to use Checkbook Import/Export tool.
          </li>
          <li className="list-disc font-semibold">
            Use Import tab in Checkbook Import/Export tool to import GL code data using .xls or .txt
            files
          </li>
        </>
      )}
    </ul>
  );
};

export const useGuideMeSteps = () => {
  const { hasEditCheckbookPermission } = useViewCheckbook();
  return [
    {
      content: <BasicPageNavigation />,
      title: (
        <CustomTitleBar
          title={
            hasEditCheckbookPermission
              ? 'Basic Page Navigation and Editing'
              : 'Basic Page Navigation'
          }
          className="font-bold text-xl text-red-600"
        />
      ),
    },
    {
      content: <ViewingData />,
      title: <CustomTitleBar title="Viewing Data" className="font-bold text-xl text-red-600" />,
    },
    {
      content: <TransactionDetails />,
      title: (
        <CustomTitleBar
          title="Transaction Details/GL Code Attribute Assignments/Export"
          className="font-bold text-xl text-red-600 whitespace-normal"
        />
      ),
    },
  ];
};

export default useGuideMeSteps;
